'use client';

/**
 * Notification Bell Component
 * 
 * Displays a bell icon with unread count badge in the header.
 * Opens notification dropdown panel when clicked.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Bell } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '@/app/store';
import { fetchUnreadCount } from '@/app/store/thunks/notificationThunks';
import { NotificationPanel } from './NotificationPanel';
import { useNotificationService } from '../hooks/useNotificationService';
import { Button } from '@/components/ui/button';

export const NotificationBell = React.memo(() => {
  const dispatch = useDispatch<AppDispatch>();
  const [isOpen, setIsOpen] = useState(false);
  const unreadCount = useSelector((state: RootState) => state.notification.unreadCount);
  const isLoading = useSelector((state: RootState) => state.notification.isLoading);
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);

  // Initialize notification service
  useNotificationService();

  // Fetch unread count on mount and periodically (only when authenticated)
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    dispatch(fetchUnreadCount());

    // Poll for unread count every 30 seconds
    const interval = setInterval(() => {
      dispatch(fetchUnreadCount());
    }, 30000);

    return () => clearInterval(interval);
  }, [dispatch, isAuthenticated]);

  // Listen for custom event to open notification center
  useEffect(() => {
    const handleOpenNotificationCenter = () => {
      setIsOpen(true);
    };

    window.addEventListener('openNotificationCenter', handleOpenNotificationCenter);

    return () => {
      window.removeEventListener('openNotificationCenter', handleOpenNotificationCenter);
    };
  }, []);

  const handleToggle = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <div className="relative">
      {/* Bell Icon Button - Matching LanguageSelector and ThemeCustomizer style */}
      <Button
        variant="ghost"
        size="sm"
        onClick={handleToggle}
        disabled={isLoading}
        className="h-8 w-8 p-0 transition-colors hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2"
        aria-label="Notifications"
        title="Notifications"
      >
        {isLoading ? (
          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
        ) : (
          <div className="relative">
            <Bell className="w-5 h-5 text-white transition-colors" />
            
            {/* Unread Count Badge */}
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-bold leading-none text-white bg-red-600 rounded-full min-w-[16px]">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </div>
        )}
      </Button>

      {/* Notification Panel */}
      {isOpen && (
        <NotificationPanel onClose={handleClose} />
      )}

      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={handleClose}
        />
      )}
    </div>
  );
});
